import {
  Card,
  DateSort,
  EmptyData,
  Table,
  TextSort,
  useHeaderHeight,
  useStyledTheme,
  TableColumn, TableStyles,
} from '@equifinance/shared';
import {
  useEffect,
  useMemo,
} from 'react';
import styled from 'styled-components';

import { DateFormatStringEnum, formatDateTime } from '@equifinance/shared/utils';
import { useApplicationsUIContext } from '../../contexts/applications/ApplicationsUIProvider';
import ApplicantColumn from './components/ApplicantColumn';
import ReferenceColumn from './components/ReferenceColumn';
import StatusColumn from './components/StatusColumn';
import UnderwriterColumn from './components/UnderwriterColumn';
import OutstandingColumn from './components/OutstandingColumn';
import DateReceivedColumn from './components/DateReceivedColumn';
import ActionsColumn from './components/ActionsColumn';
import { Application, useApplicationsContext } from '../../contexts/applications';
import ExpandableContent from './components/ExpandableContent';
import ApplicationsFilter from './components/ApplicationsFilter';

const Container = styled.div`${({ theme }) => `
  width:100%;
  margin-left: ${theme.padding[8]};
  margin-right: ${theme.padding[8]};
`}`;

const CardContainer = styled.div`${({ theme }) => `
  padding: ${theme.padding[4]};
`}`;

const mutateSearchValue = (value: string) => {
  const allowedChars = /[A-Za-z0-9\-\\/ ]/g;
  const filtered = value.match(allowedChars);
  return filtered ? filtered.join('') : '';
};

export default function ApplicationsTable() {
  const theme = useStyledTheme();

  const { applications } = useApplicationsContext();

  const headerHeight = useHeaderHeight();

  const {
    key,
    refreshData,
    loading,
    filterStatus,
    filterType,
    filterSectionHeight,
    onlyUpdatedApplications,
  } = useApplicationsUIContext();

  useEffect(() => {
    refreshData();
  }, []);

  const tableStyles: TableStyles = useMemo(() => ({
    cells: {
      style: {
        fontSize: theme.fontSize[1],
        position: 'relative',
        ':first-of-type': {
          padding: theme.padding[3],
        },
        ':not(:first-of-type)': {
          margin: `${theme.padding[3]} 0`,
          padding: `0 ${theme.padding[3]}`,
          borderLeft: `${theme.borderWidth[1]} solid ${theme.colours.primary[7]}`,
        },
      },
    },
    head: {
      style: {
        position: 'sticky',
        top: `${headerHeight + filterSectionHeight}px`,
        zIndex: 3,
      },
    },
    headRow: {
      style: {
        borderRadius: `${theme.padding[2]} ${theme.padding[2]} 0 0`,
      },
    },
    headCells: {
      style: {
        padding: theme.padding[3],
      },
    },
  }), [theme, filterSectionHeight]);

  const columns: TableColumn<Application>[] = useMemo(() => [
    {
      name: 'Reference',
      sortable: true,
      sortFunction: TextSort<Application>('reference'),
      cell: (row) => ReferenceColumn({ row }),
      width: theme.padding[11],
      conditionalCellStyles: [{
        when: (row) => !!row.tasks.find((task) => task.updated),
        style: {
          background: `${theme.colours.danger[2]} !important`,
        },
      }],
    },
    {
      name: 'Applicant',
      cell: (row) => ApplicantColumn({ row }),
    },
    {
      name: 'Status',
      sortable: true,
      sortFunction: TextSort<Application>('underwritingStatus'),
      cell: (row) => StatusColumn({ row }),
    },
    {
      name: 'Broker',
      sortable: true,
      sortFunction: TextSort<Application>('brokerageName'),
      selector: (row) => row.brokerageName || '-',
    },
    {
      name: 'Underwriter',
      sortable: true,
      sortFunction: TextSort<Application>('underwriterAssignedToName'),
      cell: (row) => UnderwriterColumn({ row }),
    },
    {
      name: 'Outstanding',
      maxWidth: theme.headerUserMenuWidth,
      cell: (row) => OutstandingColumn({ row }),
    },
    {
      name: 'Date received',
      sortable: true,
      width: theme.padding[12],
      sortFunction: DateSort<Application>('submissionDate'),
      cell: (row) => DateReceivedColumn({ row }),
    },
    {
      name: 'Last updated',
      sortable: true,
      width: theme.padding[12],
      style: {
        '> div': {
          flexGrow: 1,
          textAlign: 'center',
        },
      },
      sortFunction: DateSort<Application>('lastUpdated'),
      selector: (row) => formatDateTime(row.lastUpdated, DateFormatStringEnum.DDMMYYYY),
    },
  ], [theme]);

  const filteredApplications = useMemo(() => applications.filter((application) => {
    if (
      !!filterStatus.length
      && application.underwritingStatus
      && !filterStatus.includes(application.underwritingStatus)
    ) {
      return false;
    }
    if (
      filterType === 'MINE'
      && application.isCurrentUnderwriter === false
    ) {
      return false;
    }
    if (
      filterType === 'UNASSIGNED'
      && application.underwriterAssignedToId
    ) {
      return false;
    }
    if (
      onlyUpdatedApplications
      && !application.tasks?.find((task) => task.updated)
    ) {
      return false;
    }
    return true;
  }), [applications, filterStatus, filterType, onlyUpdatedApplications]);

  const isFiltered = !!filterStatus.length
    || onlyUpdatedApplications
    || filterType !== 'ALL';

  return (
    <Container>
      <Card>
        <CardContainer>
          <Table
            key={key}
            columns={columns}
            data={filteredApplications}
            styles={tableStyles}
            defaultSortFieldId={8}
            isFiltered={isFiltered}
            progressPending={loading}
            noDataComponent={<EmptyData message="Please start a new record." />}
            expandableRowsComponent={ExpandableContent}
            ExpandableCell={ActionsColumn}
            SubHeaderContainer={ApplicationsFilter}
            expandableColumnOptions={{
              width: theme.padding[12],
            }}
            defaultSortAsc={false}
            pagination
            searchInputProps={{
              mutateValue: mutateSearchValue,
            }}
          />
        </CardContainer>
      </Card>
    </Container>
  );
}
